<template>
    <div class="custom-picker">
        <a-popover placement="rightTop" overlayClassName="custom-picker-popover" :visible="visible">
            <template #content>
                <div class="picker-content" ref="pickerContent">
                    <div class="header">{{ $t("tables.filter") }}</div>
                    <div class="search-wrapper">
                        <a-input-search v-model="search" :placeholder="$t('message.searchItem')" />
                    </div>

                    <div class="groups">
                        <div class="group-title">
                            {{ title }}
                        </div>
                        <div class="list-item" v-for="item in getFilteredItems()" :key="item[idKey]" @click="change(item[idKey])">
                            <span class="d-flex gap-5">
                                <i class="material-icons" v-if="item.icon">{{ item.icon }}</i>
                                {{ item[labelKey] }}</span
                            >
                            <small v-if="item.description">{{ item.description }}</small>
                        </div>
                    </div>
                </div>
            </template>
            <SupsisButton
                ghost
                @click="() => (visible = !visible)"
                :id="pickerId"
                :floating-label="label"
                :focused="visible"
                :filled="hasValue"
            >
                <i class="material-icons">{{ icon }}</i>
                <span>{{ getSelectedLabel() }}</span>
                <i class="material-icons" v-if="!visible">arrow_drop_down</i>
                <i class="material-icons" v-else>arrow_drop_up</i>
            </SupsisButton>
        </a-popover>
    </div>
</template>
<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import _ from "lodash";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import log2 from "../../utils/log2";

const log = log2("CustomPicker");
export default Vue.extend({
    name: "CustomPicker",
    components: {
        SupsisButton,
    },
    props: {
        value: {
            type: [String, Object, Number, Array], // Değişiklik: Array türü eklendi
        },
        label: {
            type: String,
            default: "",
        },
        items: {
            //string or array
            type: Array,
        },
        idKey: {
            type: String,
            required: true,
        },
        labelKey: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            default: "Select item",
        },
        icon: {
            type: String,
            default: "filter_list",
        },
        multiple: {
            type: Boolean,
            default: false, // Yeni prop: varsayılan değeri false
        },
    },
    data() {
        return {
            search: "",
            clone: this.multiple ? [] : this.value, // Değişiklik: multiple ise boş array, değilse this.value
            visible: false,
            cancels: [],
            pickerId: _.uniqueId("picker_"),
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            users: (state) => state.user.users.data,
        }),
        ...mapGetters({}),
        hasValue() {
            return this.multiple ? (this.clone || []).length > 0 : !!this.clone;
        },
    },
    methods: {
        change(id) {
            if (this.multiple) {
                // Değişiklik: multiple ise array güncelleme
                const index = (this.clone || []).indexOf(id);
                if (index > -1) {
                    this.clone.splice(index, 1);
                } else {
                    this.clone = this.clone || [];
                    this.clone.push(id);
                }
            } else {
                this.clone = id;
            }
            this.visible = false;
        },
        getFilteredItems() {
            const q = (this.search || "").trim().toLowerCase();

            return this.items.filter((item) => item[this.labelKey].toLowerCase().includes(q));
        },
        getSelectedLabel() {
            log("getSelectedLabel", {
                multiple: this.multiple,
                clone: this.clone,
                items: this.items,
                idKey: this.idKey,
                labelKey: this.labelKey,
            });
            if (this.multiple) {
                // Değişiklik: multiple ise seçili öğelerin etiketlerini birleştir
                const selectedItems = this.items.filter((item) => (this.clone || []).includes(item[this.idKey]));
                return selectedItems.map((item) => item[this.labelKey]).join(", ");
            } else {
                const maybeFoundItem = this.items.find((item) => item[this.idKey] === this.clone);
                return _.get(maybeFoundItem, this.labelKey, this.title);
            }
        },
    },
    mounted() {
        const clickCallback = (e) => {
            if (
                !this.$refs.pickerContent?.contains(e.target) &&
                !document.getElementById(this.pickerId)?.contains(e.target)
            ) {
                if (this.visible) {
                    this.visible = false;
                }
            }
        };
        const keydownCallback = (e) => {
            if (e.key === "Escape") {
                if (this.visible) {
                    this.visible = false;
                }
            }
        };
        window.document.addEventListener("click", clickCallback);
        window.document.addEventListener("keydown", keydownCallback);
        this.cancels.push(() => {
            window.document.removeEventListener("click", clickCallback);
            window.document.removeEventListener("keydown", keydownCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
        this.visible = false;
    },
    watch: {
        value: {
            handler(val) {
                const isChanged = !_.isEqual(val, this.clone);
                if (isChanged) {
                    this.clone = _.cloneDeep(val);
                }
            },
            deep: true,
            immediate: true,
        },

        clone: {
            handler(val) {
                log("clone", val);
                const isChanged = !_.isEqual(val, this.value);
                if (isChanged) {
                    log("emit", val);
                    this.$emit("input", _.cloneDeep(val));
                }
            },
            deep: true,
            immediate: true,
        },
    },
});
</script>

<style lang="scss">
.custom-picker-popover {
    .ant-popover-inner-content {
        padding: 0 !important;
        box-shadow: var(--shadow-dropdown-400);
        border: 1px solid var(--color-border-default);
        border-radius: 5px;
    }

    .picker-content {
        display: flex;
        flex-direction: column;
        gap: 3px;
        width: 300px;

        .btnGroup {
            display: flex;
            border: 1px solid var(--color-border-default);
            border-radius: 6px;
            background: var(--color-bg-neutral-default);
            margin: 2px 10px;
            padding: 2px;

            .btnGrupItem {
                &.selected {
                    background: var(--color-bg-oncomponent-default);
                    box-shadow: var(--shadow-interaction-200);
                }
            }

            .supsis-button {
                border: none !important;
                font-size: 13px;
                height: 25px;
            }
        }

        .header {
            display: flex;
            flex-flow: column;
            color: var(--color-text-strong-default);
            background-color: var(--color-bg-neutral-low-default);
            outline-style: none;
            cursor: default;
            justify-content: center;
            align-items: center;
            padding: 8px;
            width: 100%;
        }

        .search-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 8px 20px;
        }

        .groups {
            display: flex;
            flex-direction: column;
            max-height: 600px;
            min-height: 300px;
            overflow-y: auto;
        }

        .group-title {
            display: flex;
            flex-flow: column;
            color: var(--color-text-strong-default);
            background-color: var(--color-bg-neutral-low-default);
            outline-style: none;
            cursor: default;

            font-weight: 600;
            justify-content: left;
            text-align: left;
            text-transform: uppercase;
            padding: 8px 12px;
            font-size: 12px;
        }

        .list-item {
            display: flex;
            flex-direction: column;
            position: relative;
            padding: 8px 12px;
            cursor: pointer;
            transition: background-color 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
            overflow-wrap: anywhere;
            background-color: var(--color-bg-default);
            font-size: 14px;
            font-weight: 500;
            color: var(--color-text-strong-default);
            align-items: flex-start;
            gap: 0px;
            border-bottom: 1px solid var(--color-border-default);

            small {
                font-size: 10px;
                color: var(--color-text-weak-default);
            }

            &:hover {
                background: var(--color-bg-hover);
            }
        }
    }
}

.custom-picker {
    display: flex;
    flex-direction: column;
}

.outlined-field {
    position: relative;
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    padding: 4px 8px;
    transition: all 0.2s ease;
    background: var(--color-bg-default);

    &.focused {
        border-color: #1890ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }

    .floating-label {
        position: absolute;
        top: -8px;
        left: 10px;
        background: var(--color-bg-default);
        padding: 0 4px;
        font-size: 12px;
        color: var(--color-text-weak-default);
        transition: all 0.2s ease;
    }

    &.filled .floating-label,
    &.focused .floating-label {
        color: #1890ff;
    }
}


</style>
